import React, { FC, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import ErrorDisplay from './components/ErrorDisplay';
import api from './api';
import './App.css';
import './components/LoadingDisplay.css';
import { formatEventTime } from './shared/date.utils';
import QuillHtmlRenderer from './components/QuillHtmlRenderer';

// Duplicating interface to resolve linter issue temporarily
interface Organization {
  id: string;
  name: string;
}

interface EventItem {
  id: string;
  name: string;
  startsAt: string;
  endsAt: string;
  place: string;
  description: string; // This now contains the Delta JSON string
  imageUrl: string;
  imageAlt: string;
  organization: Organization;
  coordinates?: { lat: number; lng: number };
}

const EventDetailsPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [event, setEvent] = useState<EventItem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const data = await api(`/events/${id}`);
        console.log('Event data received:', data); // Debug log
        
        // Convert time strings to Date objects
        data.time = new Date(data.time);
        data.endTime = new Date(data.endTime);
        setEvent(data);
      } catch (err) {
        setError('Failed to fetch event details.');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchEvent();
    }
  }, [id]);

  // Helper function to check if content might be Delta format
  const isDeltaContent = (content: string): boolean => {
    try {
      const parsed = JSON.parse(content);
      return parsed && typeof parsed === 'object' && Array.isArray(parsed.ops);
    } catch {
      return false;
    }
  };

  // Helper function to get the content to render
  const getContentToRender = (event: EventItem) => {
    // Check if description is Delta JSON
    if (event.description && isDeltaContent(event.description)) {
      console.log('Description is Delta JSON, parsing...');
      try {
        return JSON.parse(event.description);
      } catch (e) {
        console.error('Failed to parse description as Delta:', e);
      }
    }
    
    // Fallback: return null to use plain text
    console.log('Using plain text description');
    return null;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="loading-display">
          <div className="loading-spinner"></div>
          <p>Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <ErrorDisplay 
          error={error}
          title="Failed to load event details"
          onRetry={() => {
            setError(null);
            setIsLoading(true);
            // The useEffect will automatically refetch when error is cleared
          }}
        />
      </div>
    );
  }

  if (!event) {
    return <div className="page-container" style={{ textAlign: 'center' }}>{t('no_events_available', 'Event not found.')}</div>;
  }

  const contentToRender = getContentToRender(event);

  return (
    <div className="page-container event-details-page">
      <div className="event-details-header">
        <h1 className="page-title">{event.name}</h1>
        {user && (
          <div className="event-actions">
            <Link to={`/events/${event.id}/edit`} className="button-secondary">
              Edit Event
            </Link>
          </div>
        )}
      </div>
      <div className="event-details-card">
        <img src={event.imageUrl} alt={event.imageAlt} className="event-details-image" crossOrigin="anonymous"/>
        <div className="event-details-content">
          <div className="event-info-section">
            <h2>{t('event_details_title', 'Event Details')}</h2>
            <p><strong>{t('event_time_label', 'Time:')}</strong> {formatEventTime(event.startsAt, i18n.language)} - {formatEventTime(event.endsAt, i18n.language)}</p>
            <p><strong>{t('event_place_label', 'Place:')}</strong> {event.place}</p>
            {event.organization && <p><strong>{t('event_hosted_by_label', 'Hosted by:')}</strong> {event.organization.name}</p>}
          </div>
          <div className="event-description-section">
            <h2>{t('about_event_title', 'About this Event')}</h2>
            {contentToRender ? (
              <QuillHtmlRenderer 
                content={contentToRender}
                style={{
                  border: 'none',
                  padding: '0',
                  minHeight: 'auto',
                  backgroundColor: 'transparent'
                }}
              />
            ) : (
              <p>{event.description}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsPage; 